import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  useConfirmProxy,
  ProxyAuthorizationRequestStatus,
} from '@sentara-health/sentara-api-hooks-core';
import {
  createInitialProxyPopupState,
  hasActiveDesignation,
  mapAlertDetailsToPopupState,
  ProxyPopupState,
  useProxyRead,
  useProxyStatusEffect,
} from 'common';
import { GlobalContext } from 'context';
import {
  Button,
  Headings,
  ModalMultiUse,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { DesignatedRepresentative } from 'templates';
import GuestDefault from './GuestDefault';

const GuestSharedAccounts = () => {
  const {
    getFeature,
    proxyAuthorizationLinkData,
    setApprovedDesignators,
    setSharedAccessRefreshKey,
    sharedAccessRefreshKey,
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const location = useLocation();
  const { confirmProxyAuthorization } = useConfirmProxy();
  const { getProxyRead } = useProxyRead();
  const [proxyPopupState, setProxyPopupState] = useState<ProxyPopupState>(
    createInitialProxyPopupState()
  );
  const [guestProxyScreen, setGuestProxyScreen] = useState<
    'default' | 'approved' | 'denied'
  >('default');
  const [hasSharedAccounts, setHasSharedAccounts] = useState(false);
  const hasUserDismissedPopup = useRef(false);

  const hydrateApprovedGuestAccounts = useCallback(async () => {
    try {
      const readResponse = await getProxyRead();

      if (readResponse?.status !== 200) {
        setApprovedDesignators([]);
        setHasSharedAccounts(false);
        return [] as string[];
      }

      setHasSharedAccounts(hasActiveDesignation(readResponse));

      const proxies = (readResponse?.data?.proxies ?? []) as Array<
        Record<string, unknown>
      >;
      const approved = proxies.reduce(
        (acc: Array<{ name: string; status: string }>, proxy) => {
          const consents = (proxy?.Consents ?? proxy?.consents) as
            | Array<Record<string, unknown>>
            | undefined;
          const mergedConsent = (consents ?? []).reduce(
            (consentAcc, entry) => ({ ...consentAcc, ...entry }),
            {} as Record<string, unknown>
          );

          const designationStatus =
            (mergedConsent?.designationStatus as string | undefined) ?? '';
          const status =
            ((mergedConsent?.proxyAuthorizationRequestStatus ??
              mergedConsent?.ProxyAuthorizationRequestStatus) as
              | string
              | undefined) ?? '';

          if (
            designationStatus.toLowerCase() !== 'designate' ||
            status.toLowerCase() !== 'approved'
          ) {
            return acc;
          }

          const humanName = proxy.humanName as
            | { text?: string; given?: string[]; family?: string }
            | undefined;
          const name =
            humanName?.text?.trim() ||
            `${humanName?.given?.[0] ?? ''} ${humanName?.family ?? ''}`.trim();

          if (name) {
            acc.push({ name, status });
          }

          return acc;
        },
        []
      );

      setApprovedDesignators(approved);
      return approved.map((item) => item.name);
    } catch {
      setApprovedDesignators([]);
      setHasSharedAccounts(false);
      return [] as string[];
    }
  }, [getProxyRead, setApprovedDesignators]);

  const setScreenAfterProxyRead = useCallback(
    async (screen: 'default' | 'approved' | 'denied') => {
      await hydrateApprovedGuestAccounts();
      setGuestProxyScreen(screen);
    },
    [hydrateApprovedGuestAccounts]
  );

  const showDeniedGuestResponse = useCallback(
    async (memberName?: string) => {
      hasUserDismissedPopup.current = true;
      setProxyPopupState(createInitialProxyPopupState());
      await setScreenAfterProxyRead('denied');
    },
    [setScreenAfterProxyRead]
  );

  const showApprovedGuestResponse = useCallback(
    async (memberName?: string) => {
      hasUserDismissedPopup.current = true;
      setProxyPopupState(createInitialProxyPopupState());
      await setScreenAfterProxyRead('approved');
    },
    [setScreenAfterProxyRead]
  );

  useEffect(() => {
    void hydrateApprovedGuestAccounts();
  }, [hydrateApprovedGuestAccounts]);

  useEffect(() => {
    void hydrateApprovedGuestAccounts();
  }, [sharedAccessRefreshKey, hydrateApprovedGuestAccounts]);

  useEffect(() => {
    const navState = location.state as
      | { forceSharedTableView?: boolean; navTs?: number }
      | undefined;

    if (navState?.forceSharedTableView) {
      hasUserDismissedPopup.current = true;
      setProxyPopupState(createInitialProxyPopupState());
      setGuestProxyScreen('default');
      void hydrateApprovedGuestAccounts();
    }
  }, [location.state, hydrateApprovedGuestAccounts]);

  useProxyStatusEffect({
    onStatusChange: (details) => {
      if (details?.type === 'Approved') {
        void showApprovedGuestResponse(details.name);
        return;
      }

      if (details?.type === 'Terminated') {
        hasUserDismissedPopup.current = true;
        setProxyPopupState(createInitialProxyPopupState());
        void setScreenAfterProxyRead('default');
        return;
      }

      if (
        details?.type === 'Denied' ||
        details?.type === 'Expired'
      ) {
        void showDeniedGuestResponse(details.name);
        return;
      }

      if (!hasUserDismissedPopup.current) {
        setProxyPopupState(mapAlertDetailsToPopupState(details));
      }
    },
  });

  const handleProxyAction = useCallback(
    async (status: ProxyAuthorizationRequestStatus): Promise<boolean> => {
      try {
        const confirmResponse = await confirmProxyAuthorization({
          proxies: [
            {
              personaType: 'Caregiver',
              identifier: [
                {
                  system: 'designatorLoginId',
                  value: proxyAuthorizationLinkData?.forgerockUuid ?? '',
                },
                {
                  system: 'correlationId',
                  value: proxyAuthorizationLinkData?.correlationId ?? '',
                },
              ],
              Consents: [
                {
                  ProxyAuthorizationRequestStatus: status,
                },
              ],
            },
          ],
          audit: {
            InitiatedDate: new Date(),
            ModifiedDate: new Date(),
          },
        });

        if (confirmResponse?.status === 200) {
          return true;
        }

        console.error('Failed to confirm proxy authorization:', confirmResponse);
        return false;
      } catch (error) {
        console.error('Error confirming proxy authorization:', error);
        return false;
      } finally {
        // Signal the side menu to re-read shared-access state so the nav link updates without a reload.
        setSharedAccessRefreshKey?.((key: number) => key + 1);
      }
    },
    [
      confirmProxyAuthorization,
      proxyAuthorizationLinkData?.correlationId,
      proxyAuthorizationLinkData?.forgerockUuid,
      setSharedAccessRefreshKey,
    ]
  );

  const handleGuestProxyDismiss = useCallback(async () => {
    const memberName = proxyPopupState.alertDetails?.name;
    const isSuccess = await handleProxyAction(
      ProxyAuthorizationRequestStatus.Denied
    );
    if (isSuccess) {
      await showDeniedGuestResponse(memberName);
    }
  }, [
    handleProxyAction,
    proxyPopupState.alertDetails?.name,
    showDeniedGuestResponse,
  ]);

  const handleProxyModalDismiss = useCallback(async () => {
    if (proxyPopupState.showApprovalPopup) {
      const isSuccess = await handleProxyAction(
        ProxyAuthorizationRequestStatus.Pending
      );
      if (isSuccess) {
        hasUserDismissedPopup.current = true;
        setProxyPopupState(createInitialProxyPopupState());
        await setScreenAfterProxyRead('default');
      }
      return;
    }

    hasUserDismissedPopup.current = true;
    setProxyPopupState(createInitialProxyPopupState());
  }, [
    handleProxyAction,
    proxyPopupState.showApprovalPopup,
    setScreenAfterProxyRead,
  ]);

  const handleSharedTableActionComplete = useCallback(
    async (action: 'approved' | 'denied' | 'removed') => {
      if (action === 'denied') {
        hasUserDismissedPopup.current = true;
        setProxyPopupState(createInitialProxyPopupState());
        await setScreenAfterProxyRead('denied');
        return;
      }

      await setScreenAfterProxyRead('default');
    },
    [setScreenAfterProxyRead]
  );

  const shouldShowApprovalModal =
    !hasUserDismissedPopup.current &&
    proxyPopupState.showApprovalPopup &&
    proxyPopupState.alertDetails;

  const proxyModalContent =
    shouldShowApprovalModal
      ? {
          header: t('profile.designatedRepresentative.designatedRepRequestTitle'),
          body: (
            <Paragraph
              text={t('profile.designatedRepresentative.designatedRepRequestDesc', {
                fieldName: proxyPopupState.alertDetails?.name,
              })}
            />
          ),
          footer: (
            <>
              <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
                <Button
                  dataTestId="acceptProxyRequestButton"
                  variant="primary_btn"
                  className="btn-md-height w-100"
                  onClick={async () => {
                    const isSuccess = await handleProxyAction(
                      ProxyAuthorizationRequestStatus.Approved
                    );
                    if (isSuccess) {
                      await showApprovedGuestResponse(
                        proxyPopupState.alertDetails?.name
                      );
                    }
                  }}
                >
                  {t('common.accept')}
                </Button>
              </div>
              <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
                <Button
                  dataTestId="denyProxyRequestButton"
                  variant="secondary_btn"
                  className="btn-md-height w-100"
                  onClick={handleGuestProxyDismiss}
                >
                  {t('common.deny')}
                </Button>
              </div>
            </>
          ),
        }
      : null;

  const showBlankState = shouldShowApprovalModal && guestProxyScreen === 'default';
  const showApprovedState = guestProxyScreen === 'approved';
  const showDeniedState = guestProxyScreen === 'denied';
  const showSharedAccounts = !showBlankState && !showDeniedState && hasSharedAccounts;

  return (
    <>
      {getFeature?.DesignatedRepresentativeTab && proxyModalContent && (
        <ModalMultiUse
          isOpen={true}
          onClose={handleProxyModalDismiss}
          header={proxyModalContent.header}
          btnClose={true}
          variant="defaultNoPyBody"
          body={proxyModalContent.body}
          footer={proxyModalContent.footer}
          data-testid="proxyModal"
        />
      )}

      {showBlankState ? (
        <div style={{ minHeight: '320px' }} aria-hidden="true" />
      ) : (
        <div className="black-color">
          {showApprovedState && (
            <div className="mb-4">
              <Headings
                level={1}
                text={t('profile.designatedRepresentative.DRDropDownMenuItem2')}
                variant="semiBold"
                className="mb-4"
              />
              <Paragraph
                text={t('profile.designatedRepresentative.guestApprovedLandingMessage')}
                variant="regular"
                className="mb-3"
              />
            </div>
          )}

          {showDeniedState ? (
            <div className="mb-4">
              <Headings
                level={1}
                text={t('profile.designatedRepresentative.deniedTitle')}
                variant="semiBold"
                className="mb-4"
              />
              <Paragraph
                text={t('profile.designatedRepresentative.guestDeniedLandingMessage')}
                variant="regular"
                className="mb-0"
              />
            </div>
          ) : null}

          {showSharedAccounts && (
            <>
              {!showApprovedState && !showDeniedState && (
                <div className="mb-4">
                  <Headings
                    level={1}
                    text={t('profile.designatedRepresentative.DRDropDownMenuItem2')}
                    variant="semiBold"
                    className="mb-4"
                  />
                  <Paragraph
                    text={t('profile.designatedRepresentative.guestApprovedLandingMessage')}
                    variant="regular"
                    className="mb-0"
                  />
                </div>
              )}
              <DesignatedRepresentative
                viewMode="sharedOnly"
                showCollapsible={false}
                onSharedActionComplete={handleSharedTableActionComplete}
              />
            </>
          )}

          {!showSharedAccounts && !showBlankState && !showDeniedState && <GuestDefault />}
        </div>
      )}
    </>
  );
};

export default GuestSharedAccounts;
